import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stBanUser,
  stCreateOfficialAccount,
  stIdentityUser,
  stIdentityUsers,
  stLedgerEntries,
  stMemberMutesByUser,
  stAvatarDefaults,
  stSetIdentityUserMuteAllRooms,
  stSetIdentityUserRole,
  stUpdateIdentityUser,
  stUserRedpacketProfile,
  stVerifyUserInfoExists,
  StIdentityUser,
  StLedgerEntry,
  StMemberMute,
} from '../../../services/socialTradingService';
import { SocialTradingAvatarField } from '../SocialTradingAvatarField';
import {
  StIdCell,
  StActionButton,
  StActionRow,
  StBooleanChip,
  StScrollTable,
  stFormatDateTime,
  stText,
  useStConfirmAction,
} from '../StListUi';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PAGE_SIZE = 20;

const ROLE_LABEL_KEYS: Record<string, string> = {
  USER: 'module.socialTrading.roleUSER',
  OFFICIAL: 'module.socialTrading.roleOFFICIAL',
  KOL: 'module.socialTrading.roleKOL',
  AGENT: 'module.socialTrading.roleAGENT',
};

export default function SocialTradingUsersList() {
  const t = useTranslate();
  const notify = useNotify();
  const { askConfirm, confirmDialog } = useStConfirmAction();
  const [users, setUsers] = useState<StIdentityUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [roleDialog, setRoleDialog] = useState<StIdentityUser | null>(null);
  const [nextRole, setNextRole] = useState('USER');
  const [createOpen, setCreateOpen] = useState(false);
  const [createUserId, setCreateUserId] = useState('');
  const [createNickname, setCreateNickname] = useState('');
  const [createAvatar, setCreateAvatar] = useState('');
  const [userIdCheck, setUserIdCheck] = useState<'idle' | 'checking' | 'ok' | 'fail'>('idle');
  const [userDefaultAvatars, setUserDefaultAvatars] = useState<string[]>([]);
  const [detail, setDetail] = useState<StIdentityUser | null>(null);
  const [editNickname, setEditNickname] = useState('');
  const [editAvatar, setEditAvatar] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [redpacket, setRedpacket] = useState<any>(null);
  const [ledgerItems, setLedgerItems] = useState<StLedgerEntry[]>([]);
  const [memberMutes, setMemberMutes] = useState<StMemberMute[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailReqIdRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const u = await stIdentityUsers({
        role: roleFilter || undefined,
        keyword: keyword || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setUsers(u?.items || []);
      setTotal(u?.total || 0);
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [keyword, roleFilter, page, notify]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    stAvatarDefaults()
      .then((d) => setUserDefaultAvatars((d?.userAvatars || []).filter(Boolean)))
      .catch(() => setUserDefaultAvatars([]));
  }, []);

  const saveRole = async () => {
    if (!roleDialog) return;
    try {
      await stSetIdentityUserRole({ userId: roleDialog.userId, role: nextRole });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      setRoleDialog(null);
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const toggleBan = async (u: StIdentityUser) => {
    try {
      await stBanUser({ userId: u.userId, banned: !u.banned });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const toggleMuteAllRooms = async (u: StIdentityUser) => {
    try {
      const next = await stSetIdentityUserMuteAllRooms({
        userId: u.userId,
        muted: !u.mutedAllRooms,
      });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      if (detail?.userId === u.userId) {
        setDetail(next);
      }
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const openDetail = async (u: StIdentityUser) => {
    detailReqIdRef.current += 1;
    const reqId = detailReqIdRef.current;
    setDetailLoading(true);
    setDetail(u);
    setEditNickname(u.nickname || '');
    setEditAvatar(u.avatar || '');
    setRedpacket(null);
    setLedgerItems([]);
    setMemberMutes([]);
    try {
      const [idRow, rp, ledger, mutes] = await Promise.all([
        stIdentityUser(u.userId),
        stUserRedpacketProfile(u.userId).catch(() => null),
        stLedgerEntries({ userId: u.userId, limit: 20, offset: 0 }).catch(() => ({ items: [], total: 0 })),
        stMemberMutesByUser(u.userId).catch(() => []),
      ]);
      if (reqId !== detailReqIdRef.current) return;
      const row = idRow || u;
      setDetail(row);
      setEditNickname(row.nickname || '');
      setEditAvatar(row.avatar || '');
      setRedpacket(rp);
      setLedgerItems(ledger?.items || []);
      setMemberMutes(mutes || []);
    } catch (e: any) {
      if (reqId !== detailReqIdRef.current) return;
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      if (reqId === detailReqIdRef.current) {
        setDetailLoading(false);
      }
    }
  };

  const closeDetail = () => {
    detailReqIdRef.current += 1;
    setDetail(null);
    setDetailLoading(false);
    setEditNickname('');
    setEditAvatar('');
    setRedpacket(null);
    setLedgerItems([]);
    setMemberMutes([]);
  };

  const verifyCreateUserId = async (): Promise<boolean> => {
    const userId = createUserId.trim();
    if (!userId) {
      notify(t('module.socialTrading.officialNeedUserId'), { type: 'warning' });
      setUserIdCheck('fail');
      return false;
    }
    if (!UUID_RE.test(userId)) {
      notify(t('module.socialTrading.officialInvalidUserId'), { type: 'warning' });
      setUserIdCheck('fail');
      return false;
    }
    setUserIdCheck('checking');
    const ok = await stVerifyUserInfoExists(userId);
    setUserIdCheck(ok ? 'ok' : 'fail');
    if (!ok) {
      notify(t('module.socialTrading.officialUserIdNotInUserInfo'), { type: 'warning' });
    }
    return ok;
  };

  const createOfficial = async () => {
    const userId = createUserId.trim();
    if (!userId) {
      notify(t('module.socialTrading.officialNeedUserId'), { type: 'warning' });
      return;
    }
    if (!UUID_RE.test(userId)) {
      notify(t('module.socialTrading.officialInvalidUserId'), { type: 'warning' });
      return;
    }
    if (!createNickname.trim()) {
      notify(t('module.socialTrading.officialNeedNickname'), { type: 'warning' });
      return;
    }
    const nickname = createNickname.trim();
    if (nickname.length < 2 || nickname.length > 16) {
      notify(t('module.socialTrading.officialNicknameLength'), { type: 'warning' });
      return;
    }
    if (userIdCheck !== 'ok') {
      const ok = await verifyCreateUserId();
      if (!ok) return;
    }
    try {
      await stCreateOfficialAccount({
        userId,
        nickname,
        avatar: createAvatar.trim() || undefined,
      });
      setCreateOpen(false);
      setCreateUserId('');
      setCreateNickname('');
      setCreateAvatar('');
      setUserIdCheck('idle');
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const saveProfile = async () => {
    if (!detail) return;
    const nickname = editNickname.trim();
    if (nickname && (nickname.length < 2 || nickname.length > 16)) {
      notify(t('module.socialTrading.officialNicknameLength'), { type: 'warning' });
      return;
    }
    setSavingProfile(true);
    try {
      const updated = await stUpdateIdentityUser({
        userId: detail.userId,
        nickname: nickname || undefined,
        avatar: editAvatar.trim() || undefined,
      });
      setDetail(updated);
      setEditNickname(updated.nickname || '');
      setEditAvatar(updated.avatar || '');
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <PermissionGuard resource="st-users" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.users')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.users') },
          ]}
          action={
            <Button size="small" variant="outlined" onClick={load}>
              {t('module.socialTrading.refresh')}
            </Button>
          }
        />
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="h6" sx={{ flex: 1 }}>
                {t('module.socialTrading.users')}
              </Typography>
              <TextField
                size="small"
                label={t('module.socialTrading.filterKeyword')}
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const next = keywordInput.trim();
                    setKeyword(next);
                    if (page !== 0) setPage(0);
                    else if (next === keyword) load();
                  }
                }}
                sx={{ minWidth: 200 }}
              />
              <TextField
                select
                size="small"
                label={t('module.socialTrading.filterRole')}
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">{t('module.socialTrading.filterAll')}</MenuItem>
                {['USER', 'OFFICIAL', 'KOL', 'AGENT'].map((r) => (
                  <MenuItem key={r} value={r}>
                    {t(ROLE_LABEL_KEYS[r])}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                onClick={() => {
                  const next = keywordInput.trim();
                  setKeyword(next);
                  if (page !== 0) setPage(0);
                  else if (next === keyword) load();
                }}
              >
                {t('module.socialTrading.search')}
              </Button>
              {/* POST /social-trading/identity/users/official requires st-users:create */}
              <PermissionButton resource="st-users" action="create">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setCreateUserId('');
                    setCreateNickname('');
                    setCreateAvatar('');
                    setUserIdCheck('idle');
                    setCreateOpen(true);
                  }}
                >
                  {t('module.socialTrading.createOfficial')}
                </Button>
              </PermissionButton>
            </Stack>
            <StScrollTable minWidth={1100}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('module.socialTrading.avatar')}</TableCell>
                  <TableCell>{t('module.socialTrading.nickname')}</TableCell>
                  <TableCell>{t('module.socialTrading.userId')}</TableCell>
                  <TableCell>{t('module.socialTrading.userTradingVolume')}</TableCell>
                  <TableCell>{t('module.socialTrading.colRole')}</TableCell>
                  <TableCell>{t('module.socialTrading.colAccess')}</TableCell>
                  <TableCell align="right">{t('module.socialTrading.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.userId}>
                    <TableCell>
                      {u.avatar ? (
                        <Box
                          component="img"
                          src={u.avatar}
                          alt=""
                          sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{stText(u.nickname)}</TableCell>
                    <TableCell><StIdCell value={u.userId} /></TableCell>
                    <TableCell>{stText(u.userTradingVolume)}</TableCell>
                    <TableCell>
                      {ROLE_LABEL_KEYS[u.role] ? t(ROLE_LABEL_KEYS[u.role]) : stText(u.role)}
                    </TableCell>
                    <TableCell>
                      <StBooleanChip
                        value={!u.banned}
                        trueLabel={t('module.socialTrading.accessEnabled')}
                        falseLabel={t('module.socialTrading.accessDisabled')}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <StActionRow>
                        <StActionButton onClick={() => openDetail(u)}>
                          {t('module.socialTrading.viewEdit')}
                        </StActionButton>
                        {/* POST /social-trading/identity/users/role requires st-accounts:update */}
                        <PermissionButton resource="st-accounts" action="update">
                          <StActionButton
                            onClick={() => {
                              setNextRole(u.role);
                              setRoleDialog(u);
                            }}
                          >
                            {t('module.socialTrading.setRole')}
                          </StActionButton>
                        </PermissionButton>
                        {/* POST /social-trading/identity/users/ban requires st-users:update */}
                        <PermissionButton resource="st-users" action="update">
                          <StActionButton
                            color={u.banned ? 'success' : 'warning'}
                            onClick={() =>
                              askConfirm({
                                title: t('module.socialTrading.confirmActionTitle'),
                                content: t('module.socialTrading.confirmActionBody', {
                                  action: u.banned
                                    ? t('module.socialTrading.unban')
                                    : t('module.socialTrading.ban'),
                                }),
                                confirmLabel: t('module.socialTrading.confirmOk'),
                                confirmColor: u.banned ? 'success' : 'warning',
                                onConfirm: () => toggleBan(u),
                              })
                            }
                          >
                            {u.banned ? t('module.socialTrading.unban') : t('module.socialTrading.ban')}
                          </StActionButton>
                        </PermissionButton>
                      </StActionRow>
                    </TableCell>
                  </TableRow>
                ))}
                {!users.length && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        {t('module.socialTrading.emptyUsers')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </StScrollTable>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </CardContent>
        </Card>

        <Dialog open={!!roleDialog} onClose={() => setRoleDialog(null)} fullWidth maxWidth="xs">
          <DialogTitle>{t('module.socialTrading.setRole')}</DialogTitle>
          <DialogContent>
            <TextField
              select
              fullWidth
              sx={{ mt: 1 }}
              label={t('module.socialTrading.colRole')}
              value={nextRole}
              onChange={(e) => setNextRole(e.target.value)}
            >
              {['USER', 'OFFICIAL', 'KOL'].map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRoleDialog(null)}>{t('module.socialTrading.cancel')}</Button>
            {/* POST /social-trading/identity/users/role requires st-accounts:update */}
            <PermissionButton resource="st-accounts" action="update">
              <Button variant="contained" onClick={saveRole}>
                {t('module.socialTrading.save')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t('module.socialTrading.createOfficial')}</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  required
                  fullWidth
                  label={t('module.socialTrading.userId')}
                  value={createUserId}
                  onChange={(e) => {
                    setCreateUserId(e.target.value);
                    setUserIdCheck('idle');
                  }}
                  helperText={
                    userIdCheck === 'ok'
                      ? t('module.socialTrading.officialUserIdCheckOk')
                      : userIdCheck === 'fail'
                        ? t('module.socialTrading.officialUserIdNotInUserInfo')
                        : t('module.socialTrading.officialUserIdHint')
                  }
                  error={userIdCheck === 'fail'}
                  color={userIdCheck === 'ok' ? 'success' : undefined}
                />
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                  disabled={userIdCheck === 'checking'}
                  onClick={() => verifyCreateUserId()}
                >
                  {userIdCheck === 'checking'
                    ? t('module.socialTrading.officialUserIdChecking')
                    : t('module.socialTrading.officialUserIdCheck')}
                </Button>
              </Stack>
              <TextField
                size="small"
                label={t('module.socialTrading.nickname')}
                required
                value={createNickname}
                onChange={(e) => setCreateNickname(e.target.value)}
                helperText={t('module.socialTrading.createOfficialHint')}
              />
              <SocialTradingAvatarField
                value={createAvatar}
                onChange={setCreateAvatar}
                defaultUrls={userDefaultAvatars}
                uploadPath="/identity/upload"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>{t('module.socialTrading.cancel')}</Button>
            {/* POST /social-trading/identity/users/official requires st-users:create */}
            <PermissionButton resource="st-users" action="create">
              <Button variant="contained" onClick={createOfficial}>
                {t('module.socialTrading.save')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

        <Dialog open={!!detail} onClose={closeDetail} fullWidth maxWidth="md">
          <DialogTitle>{t('module.socialTrading.viewEdit')}</DialogTitle>
          <DialogContent>
            {detail && (
              <Stack spacing={2} sx={{ mt: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    color={detail.banned ? 'error' : 'success'}
                    label={
                      detail.banned
                        ? t('module.socialTrading.accessDisabled')
                        : t('module.socialTrading.statusNormal')
                    }
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {detail.userId}
                  </Typography>
                </Stack>

                <TextField
                  size="small"
                  label={t('module.socialTrading.nickname')}
                  value={editNickname}
                  onChange={(e) => setEditNickname(e.target.value)}
                  disabled={detailLoading || savingProfile}
                />
                <SocialTradingAvatarField
                  value={editAvatar}
                  onChange={setEditAvatar}
                  defaultUrls={userDefaultAvatars}
                  uploadPath="/identity/upload"
                  disabled={detailLoading || savingProfile}
                />

                <Grid container spacing={1.5}>
                  {[
                    {
                      label: t('module.socialTrading.accountType'),
                      value: ROLE_LABEL_KEYS[detail.role]
                        ? t(ROLE_LABEL_KEYS[detail.role])
                        : detail.role,
                    },
                    {
                      label: t('module.socialTrading.muteAllRooms'),
                      value: detail.mutedAllRooms
                        ? t('module.socialTrading.muteOn')
                        : t('module.socialTrading.muteNone'),
                    },
                    {
                      label: t('module.socialTrading.muteSomeRooms'),
                      value: memberMutes.length
                        ? t('module.socialTrading.muteSomeCount', { count: memberMutes.length })
                        : t('module.socialTrading.muteNone'),
                    },
                    {
                      label: t('module.socialTrading.accountStatus'),
                      value: detail.banned
                        ? t('module.socialTrading.accessDisabled')
                        : t('module.socialTrading.statusNormal'),
                    },
                    {
                      label: t('module.socialTrading.lastUpdated'),
                      value: stFormatDateTime(detail.updatedAt) || '—',
                    },
                    {
                      label: t('module.socialTrading.userTradingVolume'),
                      value: detail.userTradingVolume ?? (detailLoading ? '…' : '—'),
                    },
                    {
                      label: t('module.socialTrading.redPacketBalance'),
                      value: redpacket?.redPacketBalance ?? (detailLoading ? '…' : '—'),
                    },
                  ].map((cell) => (
                    <Grid item xs={12} sm={6} key={cell.label}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">
                            {cell.label}
                          </Typography>
                          <Typography variant="body1">{cell.value}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ flex: 1 }}>
                      {t('module.socialTrading.muteDetails')}
                    </Typography>
                    {/* POST /social-trading/identity/users/mute-all-rooms requires st-users:update */}
                    <PermissionButton resource="st-users" action="update">
                      <Button size="small" color={detail.mutedAllRooms ? 'success' : 'warning'} onClick={() => toggleMuteAllRooms(detail)}>
                        {detail.mutedAllRooms
                          ? t('module.socialTrading.unmuteAllRooms')
                          : t('module.socialTrading.muteAllRooms')}
                      </Button>
                    </PermissionButton>
                  </Stack>
                  <Stack spacing={1}>
                    {memberMutes.map((row) => (
                      <Card key={`${row.roomId}-${row.userId}`} variant="outlined">
                        <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                          <Typography variant="body2">
                            {row.roomName || row.roomId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {row.roomId} · {stFormatDateTime(row.mutedAt) || '—'}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                    {!detailLoading && !memberMutes.length && (
                      <Typography variant="body2" color="text.secondary">
                        {t('module.socialTrading.muteDetailsEmpty')}
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('module.socialTrading.redPacketClaims')}
                  </Typography>
                  <Stack spacing={1}>
                    {ledgerItems.map((row) => (
                      <Card key={row.id} variant="outlined">
                        <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                          <Typography variant="body2">
                            {row.id} · {row.amount} {row.currency}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.direction} · {stFormatDateTime(row.createdAt) || '—'}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                    {!detailLoading && !ledgerItems.length && (
                      <Typography variant="body2" color="text.secondary">
                        {t('module.socialTrading.redPacketClaimsEmpty')}
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('module.socialTrading.poolEnrollments')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('module.socialTrading.poolEnrollmentsEmpty')}
                  </Typography>
                </Box>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDetail} disabled={savingProfile}>
              {t('module.socialTrading.close')}
            </Button>
            {/* POST /social-trading/identity/users/update requires st-users:update */}
            <PermissionButton resource="st-users" action="update">
              <Button variant="contained" onClick={saveProfile} disabled={detailLoading || savingProfile}>
                {t('module.socialTrading.save')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

        {confirmDialog}
      </DashboardContent>
    </PermissionGuard>
  );
}
